import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listWardrobeCategoryCounts,
  listWardrobeItems,
  type WardrobeItem,
} from '../api/virtualUser';
import { mergeWardrobePage, sortWardrobeItems, visibleWardrobeItems } from '../lib/wardrobeFeed';

const PAGE_SIZE = 48;

type SortMode = 'default' | 'category' | 'name';

export function useWardrobeFeed(userId: string | undefined, search: string, category: string, sortMode: SortMode) {
  const [items, setItems] = useState<WardrobeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  const [appliedKey, setAppliedKey] = useState('');
  const queryKey = `${userId ?? ''}|${category}|${debouncedSearch}`;

  const genRef = useRef(0);
  const offsetRef = useRef(0);
  const inflightRef = useRef(false);
  const hasMoreRef = useRef(true);
  const userIdRef = useRef(userId);
  const searchRef = useRef(debouncedSearch);
  const categoryRef = useRef(category);

  userIdRef.current = userId;
  searchRef.current = debouncedSearch;
  categoryRef.current = category;

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 200);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const uid = userId;
    const gen = ++genRef.current;
    offsetRef.current = 0;
    hasMoreRef.current = true;
    inflightRef.current = true;
    setItems([]);
    setHasMore(true);
    if (!uid) {
      inflightRef.current = false;
      setLoading(false);
      setAppliedKey(queryKey);
      return;
    }
    setLoading(true);
    setLoadingMore(false);
    const q = debouncedSearch.trim() || undefined;
    const cat = category === 'all' ? undefined : category;
    void listWardrobeItems(uid, { search: q, category: cat, limit: PAGE_SIZE, offset: 0 })
      .then((res) => {
        if (gen !== genRef.current) return;
        setItems(mergeWardrobePage([], res.items));
        offsetRef.current = res.items.length;
        hasMoreRef.current = res.has_more;
        setHasMore(res.has_more);
        setAppliedKey(queryKey);
      })
      .catch(() => {
        if (gen !== genRef.current) return;
        setItems([]);
        setHasMore(false);
        hasMoreRef.current = false;
        setAppliedKey(queryKey);
      })
      .finally(() => {
        if (gen !== genRef.current) return;
        inflightRef.current = false;
        setLoading(false);
      });
  }, [userId, category, debouncedSearch, queryKey]);

  useEffect(() => {
    if (!userId) {
      setCounts({});
      setTotal(0);
      return;
    }
    let cancelled = false;
    void listWardrobeCategoryCounts(userId, { search: debouncedSearch.trim() || undefined })
      .then((res) => {
        if (cancelled) return;
        setCounts(res.counts);
        setTotal(res.total);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userId, debouncedSearch]);

  const loadMore = useCallback(async () => {
    const uid = userIdRef.current;
    if (!uid || inflightRef.current || !hasMoreRef.current) return;
    const gen = genRef.current;
    const offset = offsetRef.current;
    inflightRef.current = true;
    setLoadingMore(true);
    try {
      const res = await listWardrobeItems(uid, {
        search: searchRef.current.trim() || undefined,
        category: categoryRef.current === 'all' ? undefined : categoryRef.current,
        limit: PAGE_SIZE,
        offset,
      });
      if (gen !== genRef.current) return;
      setItems((prev) => mergeWardrobePage(prev, res.items));
      offsetRef.current = offset + res.items.length;
      hasMoreRef.current = res.has_more;
      setHasMore(res.has_more);
    } finally {
      if (gen === genRef.current) {
        inflightRef.current = false;
        setLoadingMore(false);
      }
    }
  }, []);

  const visible = useMemo(
    () => sortWardrobeItems(visibleWardrobeItems(items, category), sortMode),
    [items, category, sortMode]
  );

  return {
    items: visible,
    loading: loading || appliedKey !== queryKey,
    loadingMore,
    hasMore,
    counts,
    total,
    loadMore,
  };
}
